import React, { useState, createContext, useContext, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    Button,
    Form,
    Grid,
    Header,
    Menu,
    Message,
    Modal,
    ModalActions,
    ModalContent,
    ModalHeader,
    Segment
} from "semantic-ui-react";
import { ASSOCIATE_MANAGER_ROLE, ASSOCIATE_ROLES, DIRECTOR_ROLE, VP_ROLE } from "../../constants/common"
import "../../css/auth.css";

const LOGIN_URL = process.env.REACT_APP_ICC_TOOL_API_HOST + "login/";
const LOGOUT_URL = process.env.REACT_APP_ICC_TOOL_API_HOST + "logout/";
const ME_URL = process.env.REACT_APP_ICC_TOOL_API_HOST + "me/";
const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);

    useEffect(() => {
        // Load user from sessionStorage on mount
        const storedUser = sessionStorage.getItem("user");
        if (storedUser) {
            try {
                setUser(JSON.parse(storedUser));
            } catch (error) {
                console.error("Failed to parse user data:", error);
            }
        }
    }, []);

    const saveUser = (userData) => {
        sessionStorage.setItem("user", JSON.stringify(userData));
        setUser(userData);
    };

    const clearUser = () => {
        sessionStorage.removeItem("user");
        sessionStorage.removeItem('has-opened-onestop');
        sessionStorage.removeItem('onestop-progress');
        setUser(null);
    };

    const value = {
        user,
        saveUser,
        clearUser,
        isAuthenticated: !!user,
        username: user?.username || null,
        role: user?.role || '',
        allowEditsAll: user?.allowEditsAll || false,
        allowEditsLimited: user?.allowEditsLimited || false,
        userIsSafetyTeam: user?.userIsSafetyTeam || false,
        userIsMaintenanceTeam: user?.userIsMaintenanceTeam || false,
        userIsIccSupportTeam: user?.userIsIccSupportTeam || false,
        isReadOnly: user?.isReadOnly || false,
        associatedTeams: user?.associatedTeams || [],
        permissions: user?.permissions || [],
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export async function fetchWithAuth(url, options = {}) {
    try {
        const response = await fetch(url, {
            ...options,
            credentials: 'include',
        });

        if (response.status === 401) {
            sessionStorage.removeItem("user");
            sessionStorage.removeItem('has-opened-onestop');
            sessionStorage.removeItem('onestop-progress');
            window.location.href = '/login';
            return new Promise(() => {});
        }

        if (response.status === 403) {
            console.error("Request forbidden:", response);
            alert("You do not have permission to access this resource.");
            window.location.href = '/';
        }

        return response;
    } catch (error) {
        console.error("Request failed:", error);
        throw error;
    }
}

export const isReadOnly = () => {
    const user = sessionStorage.getItem("user")
    if (!user) return false
    const userObj = JSON.parse(user)
    return userObj.isReadOnly === true
}

export const isSenior = () => {
    const user = sessionStorage.getItem("user")
    if (!user) {
        return false
    }
    const userObj = JSON.parse(user)
    if (userObj.role === "Senior") {
        return true
    }
    return false
}

export const isAssociate = () => {
    const user = sessionStorage.getItem("user")
    if (!user) {
        return false
    }
    const userObj = JSON.parse(user)
    if (userObj.allowEditsLimited === false && userObj.allowEditsAll === false && userObj.role === "Associate") {
        return true
    }
}

export const isAssociateManager = () => {
    const user = sessionStorage.getItem("user")
    if (!user) {
        return false
    }
    const userObj = JSON.parse(user)
    if (userObj.allowEditsAll === true && userObj.role.toLowerCase() === ASSOCIATE_MANAGER_ROLE) {
        return true
    }
}

export const isAssociateManagerOf = (supervisorUserId) => {
    if (!supervisorUserId) return false
    const user = sessionStorage.getItem("user")
    if (!user) return false
    const userObj = JSON.parse(user)
    return userObj.role?.toLowerCase() === ASSOCIATE_MANAGER_ROLE
        && userObj.username === supervisorUserId.toLowerCase()
}

/** Submit / save draft on monthly stand-down create (role-based, not edit-all flag). */
export const canSubmitStanddownInspection = () => {
    if (isAssociate() || isSenior() || isAssociateManager()) {
        return true
    }
    const user = sessionStorage.getItem("user")
    if (!user) {
        return false
    }
    const role = (JSON.parse(user).role || '').toLowerCase()
    return role === ASSOCIATE_MANAGER_ROLE
}

export const canEditAll = () => {
    const user = sessionStorage.getItem("user")
    if (!user) return false
    const userObj = JSON.parse(user)
    if (userObj.isReadOnly === true) return false
    return userObj.allowEditsAll
}

export const canEditLimited = () => {
    const user = sessionStorage.getItem("user")
    if (!user) return false
    const userObj = JSON.parse(user)
    if (userObj.isReadOnly === true) return false
    return userObj.allowEditsLimited
}

export const userIsSafetyTeam = () => {
    const user = sessionStorage.getItem("user")
    if (!user) {
        return false
    }
    const userObj = JSON.parse(user)
    return userObj.userIsSafetyTeam
}

export const filterDataByTeam = () => {
    const user = sessionStorage.getItem("user")
    if (user) {
        const userObj = JSON.parse(user)
        const associatedTeamids = userObj.associatedTeams.map(obj => obj.id)
        if (userObj.allowEditsLimited) {
            return ({ "name": userObj.username, "filterData": true, "associatedTeamsIds": associatedTeamids })
        } else if (userObj.allowEditsLimited === false && userObj.allowEditsAll === false && userObj.userIsSafetyTeam === false && userObj.userIsIccSupportTeam === false) {
            return ({ "name": userObj.username, "filterData": true, "associatedTeamsIds": associatedTeamids })
        } else {
            return (({ "name": userObj.username, "filterData": false, "associatedTeamsIds": associatedTeamids }))
        }
    }
    return {}
}

export const isAssociateManagerOrAbove = () => {
    const user = sessionStorage.getItem("user")
    if (!user) return false
    const userObj = JSON.parse(user)
    if (userObj.isReadOnly === true) return false
    const role = userObj.role.toLowerCase()
    if (ASSOCIATE_ROLES.some((value) => value === role) && !userObj.allowEditsAll) {
        return false
    }
    return true
}

export const getCSRFToken = () => {
    const cookiePrefix = process.env.REACT_APP_COOKIE_PREFIX || 'icctool_local_';
    const cookieName = `${cookiePrefix}csrftoken=`;
    const cookieValue = document.cookie.split('; ').find(row => row.startsWith(cookieName))?.split('=')[1];
    return cookieValue || '';
}

export const getUsername = () => {
    const user = sessionStorage.getItem("user")
    if (!user) {
        return false
    }
    const userObj = JSON.parse(user)
    return userObj.username
}

export const getAssociatedTeams = () => {
    const user = sessionStorage.getItem('user')
    if (!user) return []
    return JSON.parse(user).associatedTeams || []
}

export const getUserProfileInfo = () => {
    const user = sessionStorage.getItem("user")
    if (!user) {
        return null
    }
    return JSON.parse(user)
}

export const isMaintenance = () => {
    const user = sessionStorage.getItem("user")
    if (!user) {
        return false
    }
    const userObj = JSON.parse(user)
    return userObj.role === 'Maintenance'
}

export const isMaintenanceManager = () => {
    const user = sessionStorage.getItem("user")
    if (!user) {
        return false
    }
    const userObj = JSON.parse(user)
    return userObj.role === 'Manager'
}

export const isICCSupport = () => {
    const user = sessionStorage.getItem("user")
    if (!user) {
        return false
    }
    const userObj = JSON.parse(user)
    return userObj.userIsIccSupportTeam || false
}

export const isTeamManager = () => {
    const user = sessionStorage.getItem("user")
    if (!user) {
        return false
    }
    const userObj = JSON.parse(user)
    return userObj.role === 'Manager' || userObj.role === 'Associate Manager'
}

export const isManager = () => {
    const user = sessionStorage.getItem("user")
    if (!user) {
        return false
    }
    const userObj = JSON.parse(user)
    return userObj.role === 'Manager'
}

export const isDirector = () => {
    const user = sessionStorage.getItem("user")
    if (!user) {
        return false
    }
    const userObj = JSON.parse(user)
    const role = userObj.role.toLowerCase()
    return role === DIRECTOR_ROLE
}

export const isVP = () => {
    const user = sessionStorage.getItem("user")
    if (!user) {
        return false
    }
    const userObj = JSON.parse(user)
    const role = userObj.role.toLowerCase()
    return role === VP_ROLE
}

export const canViewMaintenanceDashboard = () => {
    return isMaintenanceManager() || isMaintenance() || isICCSupport() || isTeamManager()
}

// Video Library access rules:
// - ICC Support: can view and manage (upload/edit/delete)
// - Maintenance Team, Manager, Associate Manager: can view only
// - Everyone else: no access
export const useCanViewVideoLibrary = () => {
    const { userIsIccSupportTeam, userIsMaintenanceTeam, role } = useAuth()
    return (userIsIccSupportTeam
        || userIsMaintenanceTeam
        || role === 'Manager'
        || role === 'Associate Manager')
}

export const canEditAssets = () => {
    const user = sessionStorage.getItem("user")
    if (!user) return false
    const userObj = JSON.parse(user)
    if (userObj.isReadOnly === true) return false
    return userObj.allowEditsAll || isTeamManager() || isICCSupport() || userIsSafetyTeam()
}

export const canViewManagerDashboard = () => {
    return isTeamManager() || isICCSupport() || isDirector() || isVP() || canEditAll()
}

const validateLoginCredentials = (username, password) => {
    if (!username || !password) {
        return { isValid: false, error: "Username and/or password are required." };
    }
    return { isValid: true, error: null };
};

const loginApiService = async (username, password, timeoutMs = 10000) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
        const formData = { username: username.toLowerCase(), password: password };
        const response = await fetch(LOGIN_URL, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-CSRFToken": getCSRFToken()
            },
            credentials: 'include',
            body: JSON.stringify(formData),
            signal: controller.signal
        });
        clearTimeout(timeoutId);
        return response;
    } catch (error) {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError') {
            throw new Error('Request timeout - server did not respond within 10 seconds');
        }
        throw error;
    }
};

const transformLoginResponse = (username, apiData) => {
    return {
        username: (username || apiData.username || "").toLowerCase(),
        permissions: apiData.permissions,
        role: apiData.role,
        allowEditsAll: apiData.allowEditsAll,
        allowEditsLimited: apiData.allowEditsLimited,
        associatedTeams: apiData.associatedTeams,
        userIsSafetyTeam: apiData.userIsSafetyTeam,
        userIsMaintenanceTeam: apiData.userIsMaintenanceTeam,
        userIsIccSupportTeam: apiData.userIsIccSupportTeam,
        isReadOnly: apiData.isReadOnly || false,
    };
};

export async function fetchCurrentUser() {
    const response = await fetch(ME_URL, {
        method: "GET",
        credentials: "include",
    });
    if (response.status !== 200) {
        return null;
    }
    const apiData = await response.json();
    return transformLoginResponse(apiData.username, apiData);
}

const getLoginErrorMessage = (statusCode) => {
    if (statusCode === 401) {
        return "The username or password you entered is incorrect. Please try again.";
    }
    return "An error occurred attempting to log you in. Please try again.";
};

const getNetworkErrorMessage = () => {
    return "Unable to connect to the server. Please contact the IT support team for assistance.";
};

const LoginHeader = () => (
    <Header as='h2' textAlign='center'>
        Login
    </Header>
);

const ErrorMessage = ({ error }) => {
    if (!error) return null;
    return <Message negative>{error}</Message>;
};

const UsernameField = ({ onChange }) => (
    <Form.Input
        fluid
        icon="user"
        iconPosition="left"
        placeholder="Username"
        onChange={onChange}
    />
);

const PasswordField = ({ onChange }) => (
    <Form.Input
        fluid
        icon="lock"
        iconPosition="left"
        placeholder="Password"
        type="password"
        onChange={onChange}
    />
);

const LoginButton = () => (
    <Button className="auth-button" fluid size="large">
        Login
    </Button>
);

const ForgotPasswordLink = () => (
    <Message>
        <a href='/password-reset'>Forgot your password?</a>
    </Message>
);

const useLoginHandler = (auth, navigate, redirectLocation) => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleLoginSubmit = async (event) => {
        event.preventDefault();
        setError('');

        const validation = validateLoginCredentials(username, password);
        if (!validation.isValid) {
            setError(validation.error);
            return;
        }

        setLoading(true);
        try {
            const response = await loginApiService(username, password);

            if (response.status === 200) {
                try {
                    const apiData = await response.json();
                    const userData = transformLoginResponse(username, apiData);
                    auth.saveUser(userData);
                    navigate(redirectLocation);
                } catch (jsonError) {
                    console.error("Failed to parse login response:", jsonError);
                    setError("Invalid response from server. Please contact IT support.");
                    setLoading(false);
                }
            } else {
                setError(getLoginErrorMessage(response.status));
                setLoading(false);
            }
        } catch (error) {
            console.error("Login connection error:", error);
            setError(getNetworkErrorMessage());
            setLoading(false);
        }
    };

    return {
        username,
        password,
        loading,
        error,
        setUsername,
        setPassword,
        handleLoginSubmit
    };
};

const LoginForm = ({
    loading,
    error,
    onSubmit,
    onUsernameChange,
    onPasswordChange
}) => (
    <Form size="large" onSubmit={onSubmit} loading={loading}>
        <Segment>
            <ErrorMessage error={error} />
            <UsernameField onChange={onUsernameChange} />
            <PasswordField onChange={onPasswordChange} />
            <LoginButton />
        </Segment>
    </Form>
);

export const Login = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const redirectLocation = location.state?.redirectLocation || '/';
    const auth = useAuth();

    const {
        loading,
        error,
        setUsername,
        setPassword,
        handleLoginSubmit
    } = useLoginHandler(auth, navigate, redirectLocation);

    return (
        <Grid textAlign="center" style={{ height: '75vh' }} verticalAlign='middle'>
            <Grid.Column style={{ maxWidth: 400 }}>
                <LoginHeader />
                <LoginForm
                    loading={loading}
                    error={error}
                    onSubmit={handleLoginSubmit}
                    onUsernameChange={(e) => setUsername(e.target.value)}
                    onPasswordChange={(e) => setPassword(e.target.value)}
                />
                <ForgotPasswordLink />
            </Grid.Column>
        </Grid>
    );
};

export const LogoutLink = () => {
    const [open, setOpen] = React.useState(false)
    const [error, setError] = useState('')
    let navigate = useNavigate();
    const auth = useAuth(); // Use the auth context

    const handleLogoutSubmit = async (event) => {
        event.preventDefault();
        const resp = await fetch(LOGOUT_URL, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-CSRFToken": getCSRFToken()
            },
            credentials: 'include'
        })
        if (resp.status === 200) {
            // Clear user from context and sessionStorage
            auth.clearUser();
            navigate("/")
        } else if (resp.status === 401) {
            setError("An error occurred. Please try again.")
        } else {
            setError("An error occurred. Please try again.")
        }

    }

    const openModal = () => {
        setOpen(true)
    }

    const closeModal = () => {
        setOpen(false)
    }

    return (
        <Modal
            size="mini"
            open={open}
            onClose={closeModal}
            onOpen={openModal}
            trigger={<Menu.Item position="right">Logout</Menu.Item>}
        >
            <ModalHeader>Logout</ModalHeader>
            <ModalContent>
                {error && <Message negative>{error}</Message>}
                <p>Are you sure you want to logout?</p>
            </ModalContent>
            <ModalActions>
                <Button onClick={closeModal}>
                    No
                </Button>
                <Button className="auth-button" positive onClick={handleLogoutSubmit}>
                    Yes
                </Button>
            </ModalActions>
        </Modal>
    )
}
